import { ReactNode } from 'react';
import { TableProps } from '@amzn/awsui-components-react/polaris/table';
import { Attendance, Event, Shift, Signup, WaitlistEntry } from '@amzn/red-velvet-api';
import { SignupSurface } from './constants';

/**
 * Types for the VolunteerEventShiftsTable — the volunteer-facing counterpart to
 * CoordinatorEventShiftsTable. Lives in utils/ so both the component and its pure logic
 * (volunteerEventShiftsTableUtils) share one source of truth without a components/ -> utils/ cycle.
 *
 * The same table renders on the Event Details page, the Search results cards, and
 * My Activity > Volunteer (Upcoming/Past) — so the props are intentionally page-agnostic.
 */

/**
 * The volunteer's computed status for a shift. Defined locally rather than imported from
 * @amzn/red-velvet-api: the reshaped GetWaitlistsByUser (cce-7112) no longer returns
 * ShiftWithUserStatus, so the generated client does not emit UserShiftStatus as a runtime value — and
 * this status is computed entirely client-side anyway. Values match the Smithy enum's @enumValue strings.
 */
export enum UserShiftStatus {
  NOT_SIGNED_UP = 'NOT_SIGNED_UP',
  ON_THE_WAITLIST = 'ON_THE_WAITLIST',
  SIGNED_UP = 'SIGNED_UP',
  MISSING_ATTENDANCE = 'MISSING_ATTENDANCE',
  ATTENDED = 'ATTENDED',
  DID_NOT_ATTEND = 'DID_NOT_ATTEND',
}

/** Lifecycle of a shift relative to "now" and its signup deadline. Drives the Shift Status column. */
export enum ShiftStatus {
  /** Not started and signups are still open. */
  UPCOMING = 'UPCOMING',
  /** Not started but the signup deadline has passed (waitlist territory). */
  DEADLINE_PAST = 'DEADLINE_PAST',
  /** Started but not yet ended. */
  IN_PROGRESS = 'IN_PROGRESS',
  /** Ended (or flagged complete by the backend). */
  PAST = 'PAST',
}

/**
 * The single action offered in a row's Action column. A typed action (not a display string) so the
 * component owns the label (i18n) and decides which control to render — reused ToggleSignup /
 * TrackAttendance, the new ToggleWaitlist, or a disabled placeholder for NONE.
 */
export enum ShiftAction {
  SIGN_UP = 'SIGN_UP',
  CANCEL_SIGNUP = 'CANCEL_SIGNUP',
  /** In-progress FLEXIBLE shift the user is signed up to: kebab of Cancel Sign Up / Track attendance. */
  CANCEL_OR_TRACK = 'CANCEL_OR_TRACK',
  JOIN_WAITLIST = 'JOIN_WAITLIST',
  LEAVE_WAITLIST = 'LEAVE_WAITLIST',
  TRACK_TIME = 'TRACK_TIME',
  /** No actionable control (renders a disabled "-" per the mocks). */
  NONE = 'NONE',
}

/**
 * A fully-resolved table row: the shift plus its computed statuses and action. Sorting and rendering
 * operate on this (not the raw Shift) because both need the computed values, so they are derived once.
 */
export interface VolunteerShiftRow {
  shift: Shift;
  shiftStatus: ShiftStatus;
  userStatus: UserShiftStatus;
  action: ShiftAction;
  /** The user's signup for this shift, if any. Matched once in buildShiftRow so cells don't re-find it. */
  signup?: Signup;
  /** The user's attendance record for this shift, if any. Matched once in buildShiftRow. */
  attendance?: Attendance;
  /** The user's ACTIVE waitlist entry for this shift, if any (drives the Leave action's entry id). */
  waitlistEntry?: WaitlistEntry;
}

/**
 * Props for VolunteerEventShiftsTable. Each page supplies the event, its shifts, and the current
 * user's signups/attendances/waitlist entries; the table computes per-shift user status internally
 * (the API does not return it — see GetWaitlistsByUser, cce-7112). Waitlist entries are optional so
 * the table degrades gracefully until that read API is wired (ON_THE_WAITLIST simply never lights up).
 */
export interface VolunteerEventShiftsTableProps {
  /** Owning event — provides signupDeadline (deadline source of truth), location, and complex-signup config. */
  event: Event;
  /**
   * Shifts to render. Callers pass the user-facing shifts of a published event and pre-filter as
   * needed (exclude WAITLIST / DROP_IN / unlisted shift types; My Activity splits Upcoming vs Past).
   * The table does not re-check shiftType or event status, so a Join Waitlist action assumes a
   * REGULAR shift of a published event that the backend will accept.
   */
  shifts: Shift[];
  /** The current user's signups for this event (as loaded via getSignupsByQueryParam with their alias). */
  signups: Signup[];
  /** The current user's attendance records for this event. Absent => no shift can be MISSING_ATTENDANCE. */
  attendances?: Attendance[];
  /** The current user's waitlist entries for this event. Absent/empty => ON_THE_WAITLIST never resolves. */
  waitlistEntries?: WaitlistEntry[];
  /**
   * Initial rows per page; defaults to DEFAULT_SHIFT_TABLE_PAGE_SIZE (the Event page passes a larger
   * value). This only seeds the table's page-size preference (the settings gear) — it is read once at
   * mount, so changing it at runtime has no effect.
   */
  pageSize?: number;
  /** Gear (settings) page-size options; defaults to the standard set. Search adds a 5. */
  pageSizeOptions?: number[];
  /** Optional row filter on computed user status (e.g. the Waitlists tab shows only ON_THE_WAITLIST). */
  statusFilter?: (status: UserShiftStatus) => boolean;
  /**
   * One-click join deep link (the waitlist emails' "Join waitlist" flow,
   * /view-event?id=X&shiftId=Y&action=joinWaitlist). When this matches a row's shift, the row
   * auto-triggers its CURRENT action: Sign Up when spots are open, Join Waitlist when full
   * (getShiftAction decides), covering the "spots opened since the email" case. Complex events open
   * the corresponding dialog instead of auto-firing.
   *
   * Only MOUNTED rows can auto-fire, so the target row must be on the table's first page.
   */
  forceJoinWaitlistShiftId?: string;
  /**
   * One-click leave deep link (the waitlist emails' "Leave waitlist" flow,
   * /view-event?id=X&waitlistEntryId=Y&action=leaveWaitlist — the same event-page surface as the join
   * link, per cce-4614). When this matches a row's ACTIVE waitlist entry, ToggleWaitlist auto-fires
   * the leave mutation without the confirm modal (the email click is the explicit intent).
   *
   * Same mounted-row constraint as forceJoinWaitlistShiftId.
   */
  forceLeaveWaitlistEntryId?: string;
  /**
   * One-click cancel deep link (the signup emails' "Cancel signup" flow,
   * /my-activity?shiftId=X&action=cancel). When this matches a row's shift, the row auto-fires its
   * cancel mutation — via ShiftActions for a CANCEL_SIGNUP row, or via CancelOrTrackActions for an
   * in-progress FLEXIBLE row whose action is CANCEL_OR_TRACK (cce-7837).
   *
   * Same mounted-row constraint and same once-per-mount latch as the two flags above; callers also
   * pin the owning event card first so the target row is on the first page.
   */
  forceCancelShiftId?: string;
  /**
   * Cloudscape Table variant pass-through. Defaults to 'container' (the table draws its own card
   * chrome — Event Details, Waitlists tab, My Activity cards). The Search card passes 'embedded' so
   * the table renders borderless inside the card's existing Container (per the Figma).
   */
  variant?: TableProps.Variant;
  /**
   * Which signup surface this table instance represents, tagged onto the signup-click cohort metric
   * (see SIGNUP_COHORT_METRIC). Search passes 'search', My Activity 'activity', Event Details
   * 'event-details'. Omitted where signup clicks are not compared (e.g. the Waitlists tab).
   */
  source?: SignupSurface;
  /**
   * Optional header rendered in the table's container chrome. The table uses Cloudscape's default
   * 'container' variant (it owns its own horizontal-scroll wrapper, mirroring CoordinatorEventShiftsTable),
   * so the Waitlists tab passes the event-card chrome here to render each event as its own card.
   */
  header?: ReactNode;
}
